import React, { useState, useEffect } from 'react';
import { Zap, Mail, RefreshCw, AlertCircle } from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

const AISuggestions = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestingLearning, setRequestingLearning] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  useEffect(() => {
    if (selectedSite) {
      fetchSuggestions();
    } else {
      setLoading(false);
      setError('Please select a site to view suggestions');
    }
  }, [selectedSite, user]);

  const fetchSuggestions = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/analytics/ai-suggestions.php?site_id=${selectedSite.id}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuggestions(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch suggestions');
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLearning = async () => {
    if (!selectedSite) return;

    setRequestingLearning(true);
    try {
      const response = await fetch('/app/api/analytics/request-learning.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: selectedSite.id,
          site_name: selectedSite.name,
          user_name: user.name,
          user_email: user.email
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setMailSent(true);
        setTimeout(() => setMailSent(false), 4000);
      } else {
        setError(result.message || 'Failed to send request');
      }
    } catch (error) {
      console.error('Error sending request:', error);
      setError('Network error. Please try again.');
    } finally {
      setRequestingLearning(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Zap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Site Selected</h2>
            <p className="text-gray-500">Please select a site from the dropdown to view AI suggestions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">AI Suggestions</h1>
          <p className="text-gray-600">Smart insights and recommendations for <span className="font-semibold">{selectedSite.name}</span></p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mr-3" />
              <span className="text-gray-600">Loading suggestions...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error loading suggestions</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : suggestions.length === 0 ? (
          // Empty State - No Suggestions
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">I'm still learning</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              There are no AI suggestions available for this site yet. Help us learn faster by requesting quicker learning!
            </p>
            <div className="flex flex-col gap-3 justify-center items-center">
              <button
                onClick={handleRequestLearning}
                disabled={requestingLearning || mailSent}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition ${
                  mailSent
                    ? 'bg-green-600 text-white'
                    : requestingLearning
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Mail className="w-5 h-5" />
                {mailSent ? 'Request Sent!' : 'Request Quicker Learning'}
              </button>
              {mailSent && (
                <p className="text-sm text-green-600">Admin will review your request soon</p>
              )}
            </div>
          </div>
        ) : (
          // Suggestions List
          <div className="space-y-4">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-yellow-500" />
                      <h3 className="text-lg font-semibold text-gray-800">{suggestion.title}</h3>
                      {suggestion.priority && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            suggestion.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : suggestion.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {suggestion.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-3">{suggestion.description}</p>
                    {suggestion.recommendation && (
                      <div className="bg-blue-50 border-l-2 border-blue-300 p-3 rounded">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Recommendation:</span> {suggestion.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {suggestion.created_at && (
                  <div className="mt-3 text-xs text-gray-500">
                    Added {new Date(suggestion.created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}

            {/* Request Learning Button - Also shown when there are suggestions */}
            <div className="mt-8 text-center">
              <button
                onClick={handleRequestLearning}
                disabled={requestingLearning || mailSent}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition mx-auto ${
                  mailSent
                    ? 'bg-green-600 text-white'
                    : requestingLearning
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Mail className="w-5 h-5" />
                {mailSent ? 'Request Sent!' : 'Request More Suggestions'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISuggestions;
